import React, { useState, useEffect, useRef } from 'react';
import { useCallbackOne } from 'use-memo-one';
import { ThreeLOPrompt as DefaultThreeLOPrompt } from '../../../components';
import { isAppConsentedLocally, setAppConsentLocal } from '../localAppConsent';
import type { InnerIframeProps, ComponentMap } from '../types';
import type { OverlayController } from '../../../bridge/types';

const defaultComponents: ComponentMap = {
	ThreeLOPrompt: (props) => <DefaultThreeLOPrompt {...props} />,
};

type UseInitialAppStateInput = Pick<
	InnerIframeProps,
	| 'extension'
	| 'appId'
	| 'accountId'
	| 'components'
	| 'onLoad'
	| 'setIsThreeLOPromptVisible'
	| 'setShouldShowRenderer'
> & {
	/**
	 * The shared overlay controller. `show(content, false)` is called to display
	 * ThreeLOPrompt inline (no modal transition); `hide()` is called on dismiss.
	 */
	overlayController: OverlayController;
};

type InvokeRetry = () => Promise<void>;

type UseInitialAppStateResult = {
	/**
	 * Shows the ThreeLOPrompt inline via `overlayController` and resolves after
	 * the user consents and `retry()` completes. Concurrent calls are queued and
	 * drained together after a single successful consent.
	 *
	 * Mirrors legacy `renderThreeLOPrompt` + `onThreeLO` merged into one function.
	 */
	renderThreeLOPrompt: (authUrl: string, retry: InvokeRetry) => Promise<void>;
	/**
	 * True when the app requires user consent before it can run.
	 * Iframe-based callers should gate the iframe render: `{!userConsentRequired && <iframe>}`.
	 */
	userConsentRequired: boolean;
};

/**
 * Manages the 3LO consent states shared by all Forge runtimes:
 * - **Initial consent**: gates the iframe/renderer behind `userConsentRequired`
 *   and shows ThreeLOPrompt inline via `overlayController` until the user consents.
 * - **Runtime 3LO**: exposes `renderThreeLOPrompt` for bridge methods that hit a
 *   USER_CONSENT_REQUIRED error mid-flight. Queues concurrent retries and drains
 *   them after one successful consent.
 *
 * Rendering is delegated to `overlayController` so all overlay UI (3LO prompt,
 * ExternalLinkModal, etc.) flows through a single slot in the JSX tree.
 */
export const useInitialAppState = ({
	extension,
	appId,
	accountId,
	components,
	onLoad,
	setIsThreeLOPromptVisible,
	setShouldShowRenderer,
	overlayController,
}: UseInitialAppStateInput): UseInitialAppStateResult => {
	const { ThreeLOPrompt } = components ? components(defaultComponents) : defaultComponents;

	const { consentUrl, requiresUserConsent, currentUserConsent } = extension;
	const appIsAuthenticated = isAppConsentedLocally(appId, accountId);
	const [userConsentRequired, setUserConsentRequired] = useState(
		!!(consentUrl && !currentUserConsent && requiresUserConsent && !appIsAuthenticated),
	);

	// Queue of pending retries across concurrent runtime 3LO prompts.
	const invokeRetriesRef = useRef<Array<InvokeRetry>>([]);

	// Always-current values so callbacks created inside effects and renderThreeLOPrompt
	// always reference the latest props without stale closure issues.
	const latestRef = useRef({
		ThreeLOPrompt,
		overlayController,
		setIsThreeLOPromptVisible,
		setShouldShowRenderer,
		extension,
		appId,
		accountId,
		consentUrl,
		onLoad,
	});
	latestRef.current = {
		ThreeLOPrompt,
		overlayController,
		setIsThreeLOPromptVisible,
		setShouldShowRenderer,
		extension,
		appId,
		accountId,
		consentUrl,
		onLoad,
	};

	// Shared presenter — shows <ThreeLOPrompt> via overlayController and handles
	// the common teardown (hide overlay, restore visibility + renderer) before
	// delegating to the caller-specific `onConsented` callback.
	const showThreeLOPrompt = useCallbackOne((authUrl: string, onConsented: () => Promise<void>) => {
		const {
			ThreeLOPrompt,
			overlayController,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			extension,
		} = latestRef.current;
		overlayController.show(
			<ThreeLOPrompt
				authUrl={authUrl}
				onSuccess={async () => {
					overlayController.hide();
					setIsThreeLOPromptVisible(false);
					setShouldShowRenderer?.(true);
					await onConsented();
				}}
				metadata={{ moduleType: extension.type }}
			/>,
			true,
		);
		setIsThreeLOPromptVisible(true);
		setShouldShowRenderer?.(false);
	}, []);

	useEffect(() => {
		if (!userConsentRequired) {
			return;
		}
		const { appId, accountId, consentUrl, onLoad } = latestRef.current;
		showThreeLOPrompt(consentUrl!, async () => {
			setUserConsentRequired(false);
			setAppConsentLocal(appId, accountId);
		});
		onLoad?.();
	}, [userConsentRequired, showThreeLOPrompt]);

	// Stable handler — created once, reads all values via latestRef at call time.
	const renderThreeLOPrompt = useCallbackOne((authUrl: string, retry: InvokeRetry): Promise<void> =>
		new Promise<void>((resolve) => {
			invokeRetriesRef.current.push(retry);
			showThreeLOPrompt(authUrl, async () => {
				resolve();
				try {
					await Promise.all(invokeRetriesRef.current.map((r) => r()));
				} finally {
					invokeRetriesRef.current = [];
				}
			});
		}), [showThreeLOPrompt]);

	return { userConsentRequired, renderThreeLOPrompt };
};
